import { ReactElement, useMemo } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

interface TyreDeltaBarChartApexProps {
  // Session info
  sessionDate?: string;
  sessionDescription?: string;
  airspeedKph?: number;
  riderMassKg?: number;
  airTemperatureC?: number;

  // Baseline info
  baselineTyreName?: string;
  baselinePressure?: number;

  // Plot data
  subplotData: {
    [tyreName: string]: Array<{
      x: string[];
      y: number[];
      type: string;
      name: string;
      hovertemplate: string;
      marker: any;
      showlegend: boolean;
    }>;
  };

  // Plot options
  pressureUnit: 'Bar' | 'PSI';
  deltaAverage: 'Constant' | 'Moving';

  // Equipment catalog for sorting
  equipmentCatalog?: any;

  // Optional styling
  height?: number;
  showLogo?: boolean;
}

// Helper function to find tyre width for sorting
const getTyreWidthForSorting = (tyreName: string, equipmentCatalog: any): number => {
  if (!equipmentCatalog || !equipmentCatalog.tyres) {
    return Infinity;
  }

  let searchName = tyreName;
  if (tyreName.includes(' / ')) {
    searchName = tyreName.split(' / ')[1];
  }

  const tyre = equipmentCatalog.tyres.find(
    (t: any) => t.name === searchName || t.designation === searchName || `Tyre ${t.id.slice(0, 8)}` === searchName,
  );

  return tyre?.width_mm || Infinity;
};

export default function TyreDeltaBarChartApex({
  sessionDate,
  sessionDescription,
  airspeedKph,
  riderMassKg,
  airTemperatureC,
  baselineTyreName = '',
  baselinePressure = 0,
  subplotData,
  pressureUnit,
  deltaAverage,
  equipmentCatalog,
  height = 600,
}: TyreDeltaBarChartApexProps): ReactElement {
  const chartData = useMemo(() => {
    const tyreNames = Object.keys(subplotData);

    if (tyreNames.length === 0) {
      return { series: [], options: {} as ApexOptions };
    }

    // Sort tyre names by width
    const sortedTyreNames = tyreNames.sort((a, b) => {
      const widthA = getTyreWidthForSorting(a, equipmentCatalog);
      const widthB = getTyreWidthForSorting(b, equipmentCatalog);
      return widthA - widthB;
    });

    // Collect all unique categories (pressures) across all tyres
    const allCategories = new Set<string>();
    sortedTyreNames.forEach((tyreName) => {
      const tyreData = subplotData[tyreName][0];
      tyreData.x.forEach((category: string) => allCategories.add(category));
    });
    // Sort categories numerically by pressure value
    const categories = Array.from(allCategories).sort((a, b) => parseFloat(a) - parseFloat(b));

    // Create a series for each tyre, aligned to all categories
    const series = sortedTyreNames.map((tyreName) => {
      const tyreData = subplotData[tyreName][0];
      // Build a map of pressure string -> y value
      const pressureToValue = new Map<string, number>();
      tyreData.x.forEach((x: string, idx: number) => {
        pressureToValue.set(x, tyreData.y[idx]);
      });
      // Return data aligned to sorted categories (null for missing)
      return {
        name: tyreName,
        data: categories.map((cat) => pressureToValue.get(cat) ?? null),
      };
    });

    // Calculate global y-axis range
    let globalMin = Infinity;
    let globalMax = -Infinity;

    sortedTyreNames.forEach((tyreName) => {
      const tyreData = subplotData[tyreName][0];
      tyreData.y.forEach((value: number) => {
        globalMin = Math.min(globalMin, value);
        globalMax = Math.max(globalMax, value);
      });
    });

    if (globalMin >= 0) {
      globalMin = -2;
    }

    // Calculate the required range with padding
    const dataRange = globalMax - globalMin;
    const padding = dataRange * 0.1;
    const paddedRange = dataRange + (padding * 2);

    // Round range up to nearest increment of 5
    const roundedRange = Math.ceil(paddedRange / 5) * 5;

    // Center the range around the midpoint of the data
    const dataMidpoint = (globalMax + globalMin) / 2;
    globalMin = dataMidpoint - (roundedRange / 2);
    globalMax = dataMidpoint + (roundedRange / 2);

    // Round to increments of 5 for clean axis labels
    globalMin = Math.floor(globalMin / 5) * 5;
    globalMax = Math.ceil(globalMax / 5) * 5;

    const options: ApexOptions = {
      chart: {
        type: 'bar',
        height: height,
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
        },
        fontFamily: 'Poppins',
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '50%', // Control bar width
          distributed: false,
          dataLabels: {
            position: 'top',
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: function (val: number | null) {
          if (val === null || val === undefined) return '';
          return val.toFixed(1) + 'W';
        },
        offsetY: -20,
        style: {
          fontSize: '12px',
          colors: ['#333'],
        },
      },
      stroke: {
        show: true,
        width: 1,
        colors: ['#333'],
      },
      xaxis: {
        type: 'category',
        categories: categories, // Explicitly set all pressure categories
        labels: {
          show: true,
          rotate: 0,
          rotateAlways: false,
          hideOverlappingLabels: false,
          trim: false,
          style: {
            fontSize: '12px',
            fontFamily: 'Poppins',
            fontWeight: 600,
            colors: '#333',
          },
          offsetY: - 5,
        },
        axisBorder: {
          show: true,
          color: '#333',
        },
        axisTicks: {
          show: true,
        },
      },
      yaxis: {
        title: {
          text: 'Increased Rolling Resistance (Watts)',
          style: {
            fontFamily: 'Poppins',
          },
        },
        min: globalMin,
        max: globalMax,
        labels: {
          formatter: function (val: number) {
            return val.toFixed(1);
          },
        },
      },
      grid: {
        borderColor: '#f1f1f1',
        row: {
          colors: ['#f8f9fa', 'transparent'],
          opacity: 0.5,
        },
      },
      colors: ['#2166ac', '#4393c3', '#92c5de', '#f4a582', '#d6604d'],
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'center',
        fontFamily: 'Poppins',
      },
      tooltip: {
        y: {
          formatter: function (val: number | null) {
            if (val === null || val === undefined) return '';
            return val.toFixed(3) + ' W';
          },
        },
      },
      annotations: {
        yaxis: [
          {
            y: 0,
            borderColor: 'gray',
            borderWidth: 2,
            strokeDashArray: 0,
          },
        ],
      },
    };

    return { series, options };
  }, [
    subplotData,
    pressureUnit,
    deltaAverage,
    equipmentCatalog,
    sessionDate,
    sessionDescription,
    airspeedKph,
    riderMassKg,
    airTemperatureC,
    baselineTyreName,
    baselinePressure,
    height,
  ]);

  if (!chartData.series.length) {
    return (
      <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-gray-500">
        <p>No chart data available</p>
      </div>
    );
  }

  return (
    <div className="cr-relative cr-w-full">
      {/* Title */}
      <div className="cr-text-center cr-mb-2">
        <h3 className="cr-text-base cr-font-semibold" style={{ fontFamily: 'Poppins' }}>
          Power Delta by Tyre Type and Pressure
        </h3>
      </div>

      {/* Subtitle */}
      <div className="cr-text-center cr-mb-4">
        <p className="cr-text-xs cr-text-gray-600" style={{ fontFamily: 'Poppins' }}>
          Compared to {baselineTyreName} at {baselinePressure}
          {pressureUnit.toLowerCase()} at {airspeedKph || 0} kph
          {riderMassKg && ` - Rider Mass: ${riderMassKg} kg`}
          {airTemperatureC !== undefined && airTemperatureC !== null && ` - Air Temperature: ${airTemperatureC}°C`}
            <br></br>
          {sessionDate ? new Date(sessionDate).toLocaleDateString() : 'No date'} - {sessionDescription || 'No description'} - {' '}
          {deltaAverage === 'Constant' ? 'Averaged Baseline' : 'Nearest Pair Baseline'}
        </p>
      </div>

      {/* Chart */}
      <div className="cr-relative cr-w-full" style={{ height: `${height}px` }}>
        <Chart options={chartData.options} series={chartData.series} type="bar" height={height} />
      </div>
    </div>
  );
}
